<template>
  <!-- PricePro-style filter PANEL (UAT). Rendered inline as a flex sibling of the
       grid so it PUSHES the grid over (no overlap). Filter edits update only the
       local model; nothing is applied to the grid until the user clicks Apply
       (which emits the model). The dashboard keyword search stays live/separate. -->
  <div class="ad-filter-panel d-flex flex-column" style="height:100%;">
    <v-toolbar flat dense color="toolbar" class="flex-grow-0">
      <v-icon left>mdi-filter-variant</v-icon>
      <v-toolbar-title class="text-subtitle-1 font-weight-medium">Filters</v-toolbar-title>
      <v-spacer />
      <v-btn icon @click="$emit('close')"><v-icon>mdi-close</v-icon></v-btn>
    </v-toolbar>
    <v-divider />

    <div class="pa-4 flex-grow-1" style="overflow-y:auto;">
      <!-- Ad Group is the data scope (drives the load), placed first to match the
           TPR Search drawer. Like the other filters it stages here and only
           commits to the grid/load on Apply. -->
      <v-autocomplete
        v-model="model.adGroups"
        :items="adGroupOptions"
        item-text="name"
        item-value="id"
        return-object
        auto-select-first
        label="Ad Group"
        multiple chips small-chips deletable-chips
        hide-no-data
        dense outlined hide-details background-color="input"
        prepend-inner-icon="mdi-filter-outline"
        class="mb-4" :menu-props="{ maxHeight: 260 }">
        <!-- Cap the field at two visible chips + a "+N more" summary so picking
             many ad groups doesn't grow the field and push the drawer down.
             Hidden selections still deselect from the dropdown list. -->
        <template #selection="{ item, index }">
          <v-chip
            v-if="index < 2"
            small
            close
            @click:close="removeAdGroup(item)">
            {{ item.name }}
          </v-chip>
          <span v-else-if="index === 2" class="grey--text text-caption ml-1">
            (+{{ model.adGroups.length - 2 }} more)
          </span>
        </template>
      </v-autocomplete>
      <v-divider class="mb-4" />
      <!-- Ad Date range: filter items whose ad runs during the selected period.
           Uses overlap semantics (ad_start_date <= to AND ad_end_date >= from) so
           a retailer can find ads active in a given window. Not in Sarah's spec;
           placed first among the refine filters as a primary ad-level attribute. -->
      <v-menu
        v-model="adDateMenu"
        :close-on-content-click="false"
        offset-y
        min-width="auto"
        transition="scale-transition">
        <template #activator="{ on, attrs }">
          <v-text-field
            v-bind="attrs"
            v-on="on"
            :value="adDateRangeLabel"
            label="Ad Date"
            placeholder="Select a date range…"
            prepend-inner-icon="mdi-calendar-range"
            readonly
            clearable
            dense outlined hide-details background-color="input"
            class="mb-4"
            @click:clear="model.adDateRange = []" />
        </template>
        <v-date-picker
          v-model="model.adDateRange"
          range
          no-title
          scrollable>
          <v-spacer />
          <v-btn text color="primary" @click="model.adDateRange = []">Clear</v-btn>
          <v-btn text color="primary" @click="adDateMenu = false">Done</v-btn>
        </v-date-picker>
      </v-menu>
      <!-- Filter order follows Sarah's "SEARCH COLUMNS AND FILTERS" spec (Ad Search
           tab): Ad Group, Page Name, Position, Department, Category, Subcategory,
           Vendor, Vendor ID, Subvendor, ePay/Competes, Promo Category. Feature and
           Item ID (not in her spec) are appended at the end so they aren't lost. -->
      <v-autocomplete
        v-model="model.pageNames"
        :items="pageNameOptions"
        label="Page Name"
        multiple chips small-chips deletable-chips clearable
        dense outlined hide-details background-color="input"
        class="mb-4" :menu-props="{ maxHeight: 260 }" />
      <v-autocomplete
        v-model="model.positions"
        :items="positionOptions"
        label="Position"
        multiple chips small-chips deletable-chips clearable
        dense outlined hide-details background-color="input"
        class="mb-4" :menu-props="{ maxHeight: 260 }" />
      <v-autocomplete
        v-model="model.departments"
        :items="departmentOptions"
        label="Department"
        multiple chips small-chips deletable-chips clearable
        dense outlined hide-details background-color="input"
        class="mb-4" :menu-props="{ maxHeight: 260 }" />
      <v-autocomplete
        v-model="model.categories"
        :items="categoryOptions"
        label="Category"
        multiple chips small-chips deletable-chips clearable
        dense outlined hide-details background-color="input"
        class="mb-4" :menu-props="{ maxHeight: 260 }" />
      <v-autocomplete
        v-model="model.subcategories"
        :items="subcategoryOptions"
        label="Subcategory"
        multiple chips small-chips deletable-chips clearable
        dense outlined hide-details background-color="input"
        class="mb-4" :menu-props="{ maxHeight: 260 }" />
      <v-autocomplete
        v-model="model.vendors"
        :items="vendorOptions"
        label="Vendor"
        multiple chips small-chips deletable-chips clearable
        dense outlined hide-details background-color="input"
        class="mb-4" :menu-props="{ maxHeight: 260 }" />
      <!-- Vendor ID filter moved to the backend follow-up (Project B): vendor_id
           is stripped by the response model, so it has no data to filter on yet. -->
      <v-autocomplete
        v-model="model.subvendors"
        :items="subvendorOptions"
        label="Subvendor"
        multiple chips small-chips deletable-chips clearable
        dense outlined hide-details background-color="input"
        class="mb-4" :menu-props="{ maxHeight: 260 }" />
      <v-select
        v-model="model.epayCompetes"
        :items="epayCompeteOptions"
        label="ePay / Competes"
        dense outlined hide-details background-color="input"
        class="mb-4" />
      <v-autocomplete
        v-model="model.promoCategories"
        :items="promoCategoryOptions"
        label="Promo Category"
        multiple chips small-chips deletable-chips clearable
        dense outlined hide-details background-color="input"
        class="mb-4" :menu-props="{ maxHeight: 260 }" />
      <v-text-field
        v-model="model.features"
        label="Feature"
        placeholder="Contains text..."
        clearable
        dense outlined hide-details background-color="input"
        class="mb-4" />
      <v-text-field
        v-model="model.itemIds"
        label="Item ID"
        placeholder="Contains text..."
        clearable
        dense outlined hide-details background-color="input" />
    </div>

    <v-divider />
    <div class="pa-3 d-flex align-center flex-grow-0">
      <v-btn text small :disabled="!activeCount" @click="clearAll">Clear all</v-btn>
      <v-spacer />
      <v-btn color="primary" small @click="apply">Apply</v-btn>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AdPlannerSearchFilters',
  props: {
    value: { type: Object, required: true },
    adGroupOptions: { type: Array, default: () => [] },
    pageNameOptions: { type: Array, default: () => [] },
    positionOptions: { type: Array, default: () => [] },
    departmentOptions: { type: Array, default: () => [] },
    promoCategoryOptions: { type: Array, default: () => [] },
    vendorOptions: { type: Array, default: () => [] },
    subvendorOptions: { type: Array, default: () => [] },
    categoryOptions: { type: Array, default: () => [] },
    subcategoryOptions: { type: Array, default: () => [] },
    epayCompeteOptions: { type: Array, default: () => [] }
  },
  data () {
    return { model: { ...this.value }, adDateMenu: false }
  },
  computed: {
    adDateRangeLabel () {
      const range = [...(this.model.adDateRange || [])].filter(Boolean).sort()
      if (range.length === 0) return ''
      if (range.length === 1) return `From ${range[0]}`
      return `${range[0]} – ${range[1]}`
    },
    activeCount () {
      const m = this.model
      let n = 0
      ;['adGroups', 'pageNames', 'positions', 'departments', 'categories', 'subcategories', 'vendors', 'subvendors', 'promoCategories'].forEach(k => {
        if (Array.isArray(m[k])) n += m[k].length
      })
      if (Array.isArray(m.adDateRange) && m.adDateRange.filter(Boolean).length > 0) n += 1
      if ((m.features || '').trim() !== '') n += 1
      if ((m.itemIds || '').trim() !== '') n += 1
      if (m.epayCompetes && m.epayCompetes !== 'all') n += 1
      return n
    }
  },
  watch: {
    value: {
      handler (v) { this.model = { ...v } },
      deep: true
    }
  },
  methods: {
    removeAdGroup (group) {
      this.model.adGroups = this.model.adGroups.filter(g => g?.id !== group?.id)
    },
    clearAll () {
      // Preserve the ad-group scope so clearing the refine filters doesn't empty
      // the grid (there's no admin/retailer "restore all groups" rule here, and
      // reloading every assigned group is expensive).
      this.model = { adGroups: this.model.adGroups, adDateRange: [], pageNames: [], positions: [], departments: [], categories: [], subcategories: [], promoCategories: [], vendors: [], subvendors: [], features: '', itemIds: '', epayCompetes: 'all' }
      this.emitModel()
    },
    apply () {
      this.emitModel()
      this.$emit('close')
    },
    emitModel () {
      this.$emit('input', { ...this.model })
    }
  }
}
</script>
